import React, { useEffect, useRef, useState } from 'react';

import cx from 'classnames';

import { Title6 } from '../../typography/Headings';
import styles from './inlineAccordion.module.less';
import { LinkV2 } from '../LinkV2';
import { ChevronDownIcon } from '../SvgIconsV3/ChevronDown';

export type InlineAccordionItemType = {
  title: string | JSX.Element;
  content: string | JSX.Element;
};

const InlineAccordionItem = ({ item }: { item: InlineAccordionItemType }) => {
  const { title, content } = item;
  const contentRef = useRef<HTMLDivElement>(null);
  const contentCollapseRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (!contentRef.current || !contentCollapseRef.current) {
      return;
    }

    const height = contentCollapseRef.current.clientHeight;
    contentRef.current.style.height = isOpen ? `${height}px` : '0';
  }, [isOpen]);

  return (
    <div className={cx(styles.item, { [styles.open]: isOpen })}>
      <div className={styles.title}>
        <LinkV2
          className={styles.button}
          role="button"
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          noTextTransform
          noUnderline
        >
          {typeof title === 'string' ? (
            <Title6 className={styles.buttonTitle}>{title}</Title6>
          ) : (
            title
          )}
          <ChevronDownIcon className={styles.buttonIcon} width={24} height={24} />
        </LinkV2>
      </div>
      <div className={styles.content} ref={contentRef}>
        <div className={styles.contentCollapse} ref={contentCollapseRef}>
          {content}
        </div>
      </div>
    </div>
  );
};

export type InlineAccordionProps = {
  /**
   * List of items to show in the accordion. Each item includes a `title` and `content`
   */
  items: InlineAccordionItemType[];

  /**
   * Optional class name for the container
   */
  className?: string;
};

export const InlineAccordion = (props: InlineAccordionProps) => {
  const { className, items } = props;
  const inlineAccordionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let maxHeight = 0;
    const buttons = inlineAccordionRef.current?.querySelectorAll(`.${styles.button}`) || [];
    const titles = inlineAccordionRef.current?.querySelectorAll(`.${styles.title}`) || [];
    buttons.forEach((button) => {
      if (button.clientHeight > maxHeight) {
        maxHeight = button.clientHeight;
      }
    });
    titles.forEach((title) => {
      const titleElement = title as HTMLElement;
      titleElement.style.height = `${maxHeight}px`;
    });
  }, [items]);

  return (
    <div className={cx(styles.inlineAccordion, className)} ref={inlineAccordionRef}>
      {items.map((item, index) => (
        <InlineAccordionItem item={item} key={index} />
      ))}
    </div>
  );
};
